/**
 * Utility functions for handling network errors and backend connection issues
 */

/**
 * Check if an error is a network/connection error
 */
export const isNetworkError = (error: Error): boolean => {
  return error.message.includes('Failed to fetch') ||
         error.message.includes('NetworkError') ||
         error.message.includes('Connection refused') ||
         error.message.includes('ECONNREFUSED') ||
         error.message.includes('Unable to connect to server') ||
         error.message.includes('Network connection failed') ||
         error.name === 'NetworkError' ||
         (error instanceof TypeError && error.message.includes('Failed to fetch'));
};

/**
 * Handle network errors by clearing session and redirecting to login
 */
export const handleNetworkError = (error: Error, customMessage?: string): void => {
  if (isNetworkError(error)) {
    console.error('Network error detected:', error);
    
    // Clear any stored session since backend is unreachable
    localStorage.removeItem('userSession');
    
    // Redirect to login page
    window.location.href = '/login';
    
    // Show user-friendly message if provided
    if (customMessage) {
      console.log('User message:', customMessage);
    }
  }
};

/**
 * Wrapper for async operations that might fail due to network issues
 * Automatically handles network errors and redirects to login
 */
export const withNetworkErrorHandling = async <T>(
  operation: () => Promise<T>,
  errorMessage?: string
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof Error) {
      handleNetworkError(error, errorMessage);
      
      // If it's a network error, return null (redirect will happen)
      if (isNetworkError(error)) {
        return null;
      }
    }
    
    // Re-throw non-network errors
    throw error;
  }
}; 